/**
 * IntegrationAlertBanner (Story 4.14, Task 6)
 *
 * Displays a dismissible banner showing integration health alerts across all clients.
 * Placed in the authenticated layout shell so it's visible on all pages.
 *
 * Dismiss logic: stores a hash of alert IDs+statuses in localStorage.
 * Reappears only when the alert set changes (new integration or status escalation).
 */

import { useState, useMemo, useCallback } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Warning, X } from "@phosphor-icons/react";
import { apiGet } from "../../lib/apiClient";

const DISMISS_KEY = "pl:integration-alerts-dismissed";
const MAX_VISIBLE = 5;

interface AlertItem {
  clientId: string;
  clientName: string;
  integrationId: string;
  platform: string;
  status: "warning" | "error";
  topIssue: string;
}

interface AlertsResponse {
  data: {
    warningCount: number;
    errorCount: number;
    totalCount: number;
    alerts: AlertItem[];
  };
}

/** Map platform ID to display name */
const PLATFORM_NAMES: Record<string, string> = {
  "google-analytics-4": "GA4",
  "google-search-console": "GSC",
  "google-ads": "Google Ads",
  "meta-ads": "Meta Ads",
  "youtube-analytics": "YouTube",
  "google-business-profile": "GBP",
  "google-pagespeed": "PageSpeed",
};

/** Compute a hash string for the current alert set to detect changes */
function computeAlertHash(alerts: AlertItem[]): string {
  return alerts
    .map((a) => `${a.integrationId}:${a.status}`)
    .sort()
    .join("|");
}

function getStoredDismissHash(): string | null {
  try {
    return localStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

function storeDismissHash(alerts: AlertItem[]): void {
  try {
    localStorage.setItem(DISMISS_KEY, computeAlertHash(alerts));
  } catch {
    // Non-fatal
  }
}

export function IntegrationAlertBanner() {
  const { data } = useQuery<AlertsResponse["data"]>({
    queryKey: ["integration-alerts"],
    queryFn: async () => {
      const res = await apiGet("/api/notifications/integration-alerts");
      if (!res.ok) return { warningCount: 0, errorCount: 0, totalCount: 0, alerts: [] };
      const json = (await res.json()) as AlertsResponse;
      return json.data;
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const alerts = data?.alerts ?? [];
  const totalCount = data?.totalCount ?? 0;

  const [dismissedHash, setDismissedHash] = useState<string | null>(() => getStoredDismissHash());
  const currentHash = useMemo(() => computeAlertHash(alerts), [alerts]);
  const dismissed = dismissedHash === currentHash;

  const handleDismiss = useCallback(() => {
    storeDismissHash(alerts);
    setDismissedHash(currentHash);
  }, [alerts, currentHash]);

  // Nothing to show
  if (totalCount === 0 || dismissed) {
    return null;
  }

  const visible = alerts.slice(0, MAX_VISIBLE);
  const overflow = totalCount - visible.length;

  return (
    <div
      role="alert"
      className="mx-4 mt-2 mb-0 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <Warning
            size={18}
           
            className="text-amber-600 mt-0.5 shrink-0"
          />
          <div className="space-y-1 min-w-0">
            {visible.map((alert) => (
              <p key={alert.integrationId} className="text-sm text-amber-900">
                <Link
                  to="/clients/$clientId"
                  params={{ clientId: alert.clientId }}
                  className="font-medium hover:underline"
                >
                  {alert.clientName}
                </Link>
                {" \u2014 "}
                {PLATFORM_NAMES[alert.platform] ?? alert.platform}
                {": "}
                {alert.topIssue}
              </p>
            ))}
            {overflow > 0 && (
              <p className="text-xs text-amber-700">
                and {overflow} more
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="shrink-0 text-amber-600 hover:text-amber-800 p-0.5"
          aria-label="Dismiss alerts"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
